import Button from "@/components/Button"
import DevErrorToast from "@/components/DevErrorToast"
import Link from "@/components/Link"
import OpenInNewTab from "@/components/OpenInNewTab"
import Spinner from "@/components/Spinner"
import Toast from "@/components/Toast"
import {
  useOffboardPrescription,
  useOrderDeviceMutation,
  useReopenPrescription,
} from "@/data/queries"
import { HttpError } from "@/data/requests"
import { DeviceKit } from "@/model/DeviceOrders"
import { Caregiver, Department, Organization } from "@/model/Organization"
import { UsPostalAddress } from "@/model/Patient"
import { DiabetesPrescription, OffboardReason } from "@/model/Prescription"
import OrderDeviceDialog from "@/pages/customer-support/components/OrderDeviceDialog"
import LabeledText from "@/pages/patient-details/LabeledText"
import DiabetesPrescriptionDialog from "@/pages/patient-details/prescription/DiabetesPrescriptionDialog"
import OffboardPatientDialog from "@/pages/patient-details/prescription/OffboardPatientDialog"
import OffboardingSummary from "@/pages/patient-details/prescription/OffboardingSummary"
import { buildPath, ROUTES } from "@/routes"
import cn from "@/utils/cn"
import { formatDate } from "@/utils/date-utils"
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react"
import { ComponentPropsWithRef, useState } from "react"
import { toast } from "sonner"
import { titleForDeviceDistributionMethod } from "../model"
import { caregiverName } from "../utils"

type DiabetesPrescriptionProps = {
  organization: Organization
  bloomlifeId: string
  caregivers: Caregiver[]
  departments: Department[]
  prescription: DiabetesPrescription
  patientName: string
  attCompatibility?: boolean
  shippingAddress?: UsPostalAddress
} & ComponentPropsWithRef<"div">

const DiabetesPrescriptionInfo = ({
  organization,
  bloomlifeId,
  caregivers,
  departments,
  prescription,
  patientName,
  attCompatibility,
  shippingAddress,
  className,
  ...otherProps
}: DiabetesPrescriptionProps) => {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isOffboardDialogOpen, setIsOffboardDialogOpen] = useState(false)
  const [isOrderDeviceDialogOpen, setIsOrderDeviceDialogOpen] = useState(false)

  const openEditDialog = () => setIsEditDialogOpen(true)
  const closeEditDialog = () => setIsEditDialogOpen(false)

  const {
    mutateAsync: reopenPrescription,
    isPending: isReopeningPrescription,
  } = useReopenPrescription(bloomlifeId)

  const {
    mutateAsync: offboardPrescription,
    isPending: isClosingPrescription,
  } = useOffboardPrescription(bloomlifeId)

  const { mutateAsync: orderDevice, isPending: isOrderingDevice } =
    useOrderDeviceMutation(bloomlifeId)

  const handleOrderDevice = async (kit: DeviceKit) => {
    try {
      await orderDevice({
        prescriptionId: prescription.id,
        vendor: kit.vendor,
        deviceKitId: kit.id,
      })
      toast.custom((toastId) => (
        <Toast toastId={toastId} description="Device ordered successfully." />
      ))
      setIsOrderDeviceDialogOpen(false)
    } catch (error) {
      if ((error as HttpError)?.statusCode === 422) {
        toast.custom((toastId) => (
          <Toast
            toastId={toastId}
            variant="error"
            description="Invalid patient shipping address!"
          />
        ))
      } else if ((error as HttpError)?.statusCode === 400) {
        toast.custom((toastId) => (
          <Toast
            toastId={toastId}
            variant="error"
            description="Patient without a shipping address!"
          />
        ))
      } else {
        toast.custom(
          (toastId) => (
            <DevErrorToast
              toastId={toastId}
              userMessage="Something went wrong while ordering the device. Try again, or contact the dev team."
              error={error}
            />
          ),
          { duration: Infinity }
        )
      }
    }
  }

  const onClosePrescriptionSelected = () => {
    setIsOffboardDialogOpen(true)
  }

  const handleOffboardConfirm = async (
    reason: OffboardReason,
    note: string
  ) => {
    try {
      await offboardPrescription({
        prescriptionId: prescription.id,
        reason,
        note: note || undefined,
      })
      toast.custom((toastId) => (
        <Toast toastId={toastId} description="Diabetes prescription closed" />
      ))
      setIsOffboardDialogOpen(false)
    } catch (error) {
      toast.custom((toastId) => (
        <DevErrorToast
          toastId={toastId}
          userMessage="Something went wrong while closing this prescription. Try again, or contact the dev team."
          error={error}
        />
      ))
    }
  }

  const onReopenPrescriptionSelected = async () => {
    try {
      await reopenPrescription(prescription.id)
      toast.custom((toastId) => (
        <Toast toastId={toastId} description="Diabetes prescription reopend" />
      ))
    } catch (error) {
      toast.custom((toastId) => (
        <DevErrorToast
          toastId={toastId}
          userMessage="Something went wrong while reopening this prescription. Try again, or contact the dev team."
          error={error}
        />
      ))
    }
  }

  const caregiver = caregivers.find(
    (caregiver) => caregiver.id === prescription.caregiverId
  )

  const department = departments.find(
    (department) => department.id === prescription.caregiverDepartmentId
  )?.name

  const isActive = prescription.status === "active"

  return (
    <div
      className={cn(
        "rounded-xl bg-white p-6 shadow-md shadow-black/5",
        className
      )}
      {...otherProps}
    >
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex flex-col">
            <div className="flex items-center gap-3">
              <p className="text-xl font-extrabold">Diabetes</p>
              <span
                className={cn(
                  "cursor-default rounded-full border px-3 py-1 text-xs font-semibold",
                  isActive
                    ? "border-blue-200 bg-blue-100 text-blue-700"
                    : "border-gray-200 bg-gray-100 text-gray-500"
                )}
              >
                {isActive ? "Active" : "Offboarded"}
              </span>
              <span
                className={cn(
                  "material-icons-outlined cursor-pointer text-[24px] text-primary/60 transition-colors hover:text-primary",
                  isClosingPrescription || isReopeningPrescription
                    ? "pointer-events-none cursor-not-allowed text-primary/30 hover:text-primary/30"
                    : ""
                )}
                onClick={openEditDialog}
              >
                edit
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isActive && (
            <Button
              variant="primary"
              className="px-6 py-1.5"
              onClick={() => setIsOrderDeviceDialogOpen(true)}
            >
              Order device
            </Button>
          )}
          <Link
            type="button"
            to={buildPath(ROUTES.POPULATION.PATIENT_READINGS, {
              bloomlifeId,
              prescriptionId: prescription.id,
            })}
            className="group flex items-center gap-3 bg-primary px-6 text-white hover:bg-primary/90"
          >
            <span className="group-hover:group-has-[.icon-hover:hover]:underline">
              View readings
            </span>
            <OpenInNewTab
              url={buildPath(ROUTES.POPULATION.PATIENT_READINGS, {
                bloomlifeId,
                prescriptionId: prescription.id,
              })}
            />
          </Link>
          <Popover className="relative">
            <PopoverButton className="flex h-[38px] w-[38px] items-center justify-center rounded-md border border-gray-300 bg-white focus-visible:outline-none">
              <span
                className={cn(
                  "material-icons cursor-pointer text-primary/60 transition-colors hover:text-primary",
                  isClosingPrescription || isReopeningPrescription
                    ? "pointer-events-none cursor-not-allowed text-primary/30"
                    : ""
                )}
              >
                more_vert
              </span>
            </PopoverButton>
            <PopoverPanel
              anchor="bottom end"
              className="flex w-56 flex-col rounded-lg border border-primary/20 bg-white shadow-lg"
            >
              {isActive ? (
                <div
                  className="flex cursor-pointer items-center gap-2 rounded-lg px-4 py-2 text-[16px] transition hover:bg-primary/5"
                  onClick={onClosePrescriptionSelected}
                >
                  {isClosingPrescription && <Spinner className="size-4" />}
                  <span className="text-red-600">Offboard patient</span>
                </div>
              ) : (
                <div
                  className="flex cursor-pointer items-center gap-2 rounded-lg px-4 py-2 text-[16px] transition hover:bg-primary/5"
                  onClick={onReopenPrescriptionSelected}
                >
                  {isReopeningPrescription && <Spinner className="size-4" />}
                  <span>Reopen prescription</span>
                </div>
              )}
            </PopoverPanel>
          </Popover>
        </div>
      </div>
      {!isActive && prescription.offboarding && (
        <OffboardingSummary
          reason={prescription.offboarding.reason}
          note={prescription.offboarding.note}
          offboardedBy={prescription.offboarding.offboardedBy}
          offboardedAt={prescription.offboarding.offboardedAt}
          source={prescription.offboarding.source}
        />
      )}
      <div className="grid grid-cols-4 gap-4">
        <LabeledText
          label="Prescription start date"
          value={
            prescription.startDate &&
            formatDate(prescription.startDate, "MM/dd/yyyy")
          }
        />
        <LabeledText
          label="Prescription end date"
          value={
            prescription.endDate &&
            formatDate(prescription.endDate, "MM/dd/yyyy")
          }
        />
        <LabeledText
          className="col-span-2"
          label="Diagnosis"
          value={prescription.diagnosis}
        />
        <LabeledText
          label="Clinical protocol"
          value={prescription.clinicalProtocolName}
        />
        <LabeledText label="Provider" value={caregiverName(caregiver)} />
        <LabeledText label="Department" value={department} />
        <LabeledText
          label="Device distribution"
          value={titleForDeviceDistributionMethod(
            prescription.deviceDistributionMethod
          )}
        />
      </div>
      <DiabetesPrescriptionDialog
        isOpen={isEditDialogOpen}
        onClose={closeEditDialog}
        organization={organization}
        bloomlifeId={bloomlifeId}
        prescription={prescription}
        caregivers={caregivers}
        departments={departments}
      />
      <OffboardPatientDialog
        isOpen={isOffboardDialogOpen}
        onClose={() => setIsOffboardDialogOpen(false)}
        onConfirm={handleOffboardConfirm}
        isSubmitting={isClosingPrescription}
      />
      <OrderDeviceDialog
        isOpen={isOrderDeviceDialogOpen}
        onClose={() => setIsOrderDeviceDialogOpen(false)}
        patientName={patientName}
        shippingAddress={shippingAddress}
        attCompatibility={attCompatibility}
        diagnosis={prescription.diagnosis || "-"}
        onOrder={handleOrderDevice}
        isOrdering={isOrderingDevice}
        prescriptionTypes={["diabetes"]}
      />
    </div>
  )
}

export default DiabetesPrescriptionInfo
